import {useEffect, useState} from 'react';
import close from './icons/close.svg';

import styles from './ImageView.module.scss';

interface Props {
  show?: boolean;
  onClose: () => any;
  images: string[];
  selectedImageIndex?: number;
}

function ImageView({
  show,
  onClose,
  images = [],
  selectedImageIndex = 0,
}: Props) {
  const [currentIndex, setCurrentIndex] = useState(selectedImageIndex);
  // Reset to first image when modal opens
  useEffect(() => {
    if (show) {
      setCurrentIndex(selectedImageIndex);
    }
  }, [show, selectedImageIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentIndex, images.length, selectedImageIndex]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (!show || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        {/* Close button */}
        <img
          className={styles.close}
          alt="close"
          src={close}
          onClick={onClose}
        />

        {/* Navigation buttons */}
        {images.length > 1 && (
          <>
            <button
              className={styles.navButton}
              onClick={goToPrevious}
              aria-label="Previous image"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              className={styles.navButton}
              onClick={goToNext}
              aria-label="Next image"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        {/* Image container */}
        <div className={styles.imageContainer}>
          <img
            className={styles.image}
            alt={`${currentIndex + 1} of ${images.length}`}
            src={currentImage}
          />
        </div>

        {/* Image counter */}
        {images.length > 1 && (
          <div className={styles.counter}>
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageView;
